import { useState, useEffect, useCallback, createContext, useContext, memo, useMemo, useRef } from "react";
import {
    ButtonBase, createTheme, Stack,
    Box, Grid,
    Radio, RadioGroup, FormControlLabel,
    Paper,
    Typography,
    TableContainer
} from "@mui/material";
import GridOnRoundedIcon from "@mui/icons-material/GridOnRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import TodayIcon from '@mui/icons-material/Today';
import "../InventoryDetail.css";
import { ConditionalWrapper } from "../../../../Components/utils/ConditionalWrapper";
import { ThemeProvider } from "@mui/material/styles";
import { fetchInventoryTimeline } from "../../../../services/Inventory/InventoryDetailServices";
import WhereUsedRenderChart from "./WhereUsedRenderChart";
import { AlternateLocationRenderTable } from "./AlternateLocationRenderTable";
import SectionSpinner from "../../../../CommonComponents/common/Loader/SectionSpinner"

const theme = createTheme({
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    backgroundColor: "#2b0920ff",
                    color: "#ffffff",
                    borderRadius: "5px",
                    paddingTop: "2px",
                    paddingBottom: "2px",
                    paddingLeft: "8px",
                    paddingRight: "8px",
                    fontSize: "13px",
                    textTransform: "none",
                    height: '28px',
                    "&:hover": {
                        backgroundColor: "#614b5aff"
                    }
                }
            }
        }
    }
});

const timeframeValues = ["Q1FY26", "Q2FY26", "Q3FY26", "Q4FY26", "Q1FY27"];

export function WhereUsedGraphView({ inventoryId, siteCode }) {
    const [isLoading, setIsLoading] = useState({ data: true, error: false });
    const [columns, setColumns] = useState([]);

    const [isActive, setIsActive] = useState({
        table: false,
        chart: false,
        hybrid: true
    });
    const [rows, setRows] = useState({});
    const [editableRows, setEditableRows] = useState({});
    const [timeframe, setTimeframe] = useState("Q1FY26");       // default API value
    const [showTimeOptions, setShowTimeOptions] = useState(false);
    const [selectedChildId, setSelectedChildId] = useState(null);
    const ref = useRef(null);

    useEffect(() => {
        setIsLoading({ data: false });
        setEditableRows({});
    }, [timeframe, selectedChildId]);

    useEffect(() => {
        let isCurrent = true;                                   // flag for stale calls

        const fetchData = async () => {
            const apiResponse = await fetchInventoryTimeline(
                selectedChildId || inventoryId,
                siteCode,
                "HYBRID",
                timeframe,
                false
            );
            if (!isCurrent) return;                             // Skip stale result

            // On API error
            if (apiResponse.error) {
                setIsLoading({ data: false, error: true });     // table hidden
                return;
            }
            setRows(prev => ({
                ...prev,
                [selectedChildId || inventoryId]: apiResponse.measures
            }));
            // Set editable rows to baseline initially
            setEditableRows(JSON.parse(JSON.stringify(apiResponse.measures)));
            if (apiResponse.headers && Array.isArray(apiResponse.headers)) {
                const formattedColumns = apiResponse.headers.map(h => `${h.displayDate}\n ${h.fiscalLabel}`);
                setColumns(formattedColumns);
            }
            setIsLoading({ data: true, error: false });
            return;
        };
        fetchData();
        return () => {
            isCurrent = false;                                 // Cleanup → in-flight request becomes stale
        };
    }, [inventoryId, siteCode, timeframe, selectedChildId]);

    const fallbackColumns = useMemo(
        () => (columns.length ? columns : ['I', 'II', 'III', 'IV', 'V']),
        [columns]
    );

    const fallbackRows = useMemo(() => ([
        [inventoryId,
            {
                "Total Supply": {
                    name: "Total Supply",
                    quantities: Array.from({ length: fallbackColumns.length }, () => 0)
                },
                "Total Demand": {
                    name: "Total Demand",
                    quantities: Array.from({ length: fallbackColumns.length }, () => 0)
                },
                "Projected Inventory": {
                    name: "Projected Inventory",
                    quantities: Array.from({ length: fallbackColumns.length }, () => 0)
                }
            }
        ]
    ]), [inventoryId, fallbackColumns.length]);

    const displayRows = useMemo(() => {
        const ordered = [];
        if (rows[inventoryId]) {
            ordered.push([inventoryId, rows[inventoryId]]);
        }
        if (selectedChildId && rows[selectedChildId]) {
            ordered.push([selectedChildId, rows[selectedChildId]]);
        }
        return ordered;
    }, [rows, inventoryId, selectedChildId]);

    const onTimeframeChange = (value) => {
        setTimeframe(value);
        setShowTimeOptions(false);
    }

    const handleItemSelect = (nodeData) => {
        if (nodeData.id !== inventoryId) {
            setSelectedChildId(nodeData.id);
        }
    };

    const TimeframeOptions = () => {
        return (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, position: "sticky", left: 0 }}>
                <Paper
                    elevation={3}
                    sx={{
                        paddingLeft: 2, paddingBottom: 0.5,
                        backgroundColor: "#fafafa",
                        border: "1px solid #ccc",
                        borderRadius: "8px"
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{ mb: 0.7, fontWeight: "bold", color: "#6d6c6cff" }}
                    >
                        Select Timeframe
                    </Typography>

                    <RadioGroup
                        row
                        onChange={(e) => onTimeframeChange(e.target.value)}
                        sx={{ flexDirection: "row", flexWrap: "nowrap" }}
                    >
                        {timeframeValues.map((opt) => (
                            <FormControlLabel
                                checked={timeframe === opt}
                                key={opt}
                                value={opt}
                                control={
                                    <Radio
                                        sx={{
                                            "& .MuiSvgIcon-root": { fontSize: "1.2rem" },
                                            p: 0,
                                            color: "#969393ff",
                                            "&.Mui-checked": { color: "#000" }
                                        }}
                                    />
                                }
                                label={opt}
                                sx={{
                                    "& .MuiFormControlLabel-label": {
                                        fontSize: "0.8rem",
                                        color: "#000",
                                        marginLeft: 0.2
                                    },
                                    marginRight: 2.5,
                                }}
                            />
                        ))}
                    </RadioGroup>
                </Paper>
            </Box>
        )
    }

    return (
        <div style={{ width: '100%' }} ref={ref}>
            <ThemeProvider theme={theme}>
                <Grid container sx={{ marginBottom: '10px', position: "sticky", left: 0, alignItems: 'center', mr: 2 }}>
                    <Grid size={{ xs: 6 }}>
                        <Stack direction="row" gap={1}>

                        </Stack>
                    </Grid>
                    <Grid container size={{ xs: 6 }} justifyContent='flex-end'>
                        <Stack direction="row-reverse" columnGap={2}>
                            <ButtonBase
                                onClick={() =>
                                    setIsActive({ table: true })
                                }
                            >
                                <GridOnRoundedIcon color={isActive.table ? "primary" : ""} />
                            </ButtonBase>
                            <ButtonBase
                                onClick={() =>
                                    setIsActive({ chart: true })
                                }
                            >
                                <TimelineRoundedIcon color={isActive.chart ? "primary" : ""} />
                            </ButtonBase>
                            <ButtonBase
                                onClick={() =>
                                    setIsActive({ hybrid: true })
                                }
                            >
                                <QueryStatsIcon color={isActive.hybrid ? "primary" : ""} />
                            </ButtonBase>
                            <ButtonBase
                                size="large"
                                onClick={() => setShowTimeOptions(!showTimeOptions)}
                            // sx={{
                            //   borderColor: "#808080",
                            //   color: "#000",
                            //   backgroundColor: "#f5f5f5",
                            //   "&:hover": { backgroundColor: "#d3d3d3" }
                            // }}
                            >
                                <TodayIcon color={showTimeOptions ? 'primary' : ''} />
                            </ButtonBase>
                        </Stack>
                    </Grid>
                </Grid>
                <ConditionalWrapper condition={showTimeOptions}>
                    <TimeframeOptions />
                </ConditionalWrapper>
            </ThemeProvider>

            <HighlightMarkProvider>
                <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
                    <ConditionalWrapper condition={isActive.hybrid || isActive.chart}>
                        <WhereUsedRenderChart
                            siteCode={siteCode}
                            item={inventoryId}
                            onNodeClick={handleItemSelect}
                            selectedNodeId={selectedChildId}
                        />
                    </ConditionalWrapper>
                    <Typography align="center" sx={{ my: 1 }}>
                        Projections
                    </Typography>
                    <ConditionalWrapper condition={(isActive.table || isActive.hybrid)}>
                        <Box sx={{ position: "relative", width: "100%" }}>
                            <AlternateLocationRenderTable
                                columns={columns.length ? columns : fallbackColumns}
                                rows={displayRows.length ? displayRows : fallbackRows}
                                editedRows={displayRows.length ? displayRows : fallbackRows}
                                inventoryId={inventoryId}
                                label="whereUsed"
                            />
                            {!isLoading.data && <SectionSpinner source="WhereUsedGraphView" />}
                        </Box>
                    </ConditionalWrapper>
                </TableContainer>
            </HighlightMarkProvider>
        </div >
    );
}

const HighlightMarkContext = createContext({
    highlightMMarkIdx: -1,
    setHighlightMarkIdx: () => { }
})

export const useHighlightMarkIdx = () => useContext(HighlightMarkContext)

const HighlightMarkProvider = memo(({ children }) => {
    const [highlightMarkIdx, setHighlightMarkIdx] = useState(-1)
    const updateHighlightMarkIdx = useCallback((idx) => setHighlightMarkIdx(idx), [])
    const contextValue = useMemo(() => ({ highlightMarkIdx, setHighlightMarkIdx: updateHighlightMarkIdx }), [highlightMarkIdx, updateHighlightMarkIdx]);

    return (
        <HighlightMarkContext.Provider value={contextValue}>
            {children}
        </HighlightMarkContext.Provider>
    )
})
